/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useRef, useState } from 'react'
import type {
    GroupByType,
    SwingTimingData,
} from 'leaderboard/scripts/swing-path/types/globals'
import { normalizeAccents } from '../utils/normalize-accents'

import PlayerCard from './PlayerCard.tsx'
import type { GroupByLogic } from './utils/do-group-by-logic.ts'
import { MIN_SWINGS } from './utils/constants.ts'
import getShortPitchName from '../utils/get-short-pitch-name.ts'
import getTypeText from './utils/get-type-text.ts'

type PlayerSearchProps = {
    data: SwingTimingData[]
    onAdd: (item: SwingTimingData) => void
    selectedUniqueIds: string[]
    isFull: boolean
    groupBys: string[]
    groupByLogic: GroupByLogic
    type: GroupByType
}

const PAGE_SIZE = 8

export default function PlayerSearch({
    data,
    onAdd,
    selectedUniqueIds,
    isFull,
    groupByLogic,
    type,
}: PlayerSearchProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [pitchTypeFilter, setPitchTypeFilter] = useState('')
    const [pitchHandFilter, setPitchHandFilter] = useState('')
    const [searchFilter, setSearchFilter] = useState('')
    const [pageNumber, setPageNumber] = useState(0)

    const typeText = getTypeText(type)

    const hasPitchTypes = Boolean(data[0].api_pitch_type_group03)
    const pitchTypes = hasPitchTypes
        ? [...new Set(data.map((d) => d.api_pitch_type_group03))].sort((a, b) =>
              getShortPitchName(a) > getShortPitchName(b) ? 1 : -1
          )
        : []

    const needle = normalizeAccents(searchFilter)
    const filtered = data
        .slice()
        .filter((d) => d.n_swings >= MIN_SWINGS)
        .sort((a, b) => {
            if (a.name.toUpperCase() !== b.name.toUpperCase()) {
                return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
            }

            if (a.year !== b.year && a.year && b.year) {
                return b.year - a.year
            }

            if (a.api_pitch_type_group03 !== b.api_pitch_type_group03) {
                return getShortPitchName(a.api_pitch_type_group03) >
                    getShortPitchName(b.api_pitch_type_group03)
                    ? 1
                    : -1
            }

            return 0
        })
        .filter((d) => {
            if (pitchHandFilter === '') {
                return true
            }

            return d.pitch_hand_type === pitchHandFilter
        })
        .filter((d) => {
            if (pitchTypeFilter === '') {
                return true
            }

            return d.api_pitch_type_group03 === pitchTypeFilter
        })
        .filter((d) => {
            if (searchFilter === '') {
                return true
            }

            const haystack = normalizeAccents(d.name)

            return haystack.includes(needle)
        })

    const sliced = filtered.slice(
        pageNumber * PAGE_SIZE,
        pageNumber * PAGE_SIZE + PAGE_SIZE
    )

    const pages = Math.ceil(filtered.length / PAGE_SIZE)
    const selectedSet = new Set(selectedUniqueIds)

    const handleSelect = useCallback(
        (d: SwingTimingData) => {
            if (selectedSet.has(d.uniqueId)) {
                return
            }

            if (ref.current) {
                ref.current.hidePopover()
            }

            onAdd(d)
        },
        [onAdd, selectedSet]
    )

    function handleFilter(
        filterType: 'pitchType' | 'pitchHand' | 'search',
        value: string
    ) {
        switch (filterType) {
            case 'pitchHand':
                setPitchHandFilter(value)
                break
            case 'pitchType':
                setPitchTypeFilter(value)
                break
            case 'search':
            default:
                setSearchFilter(value)
                break
        }

        setPageNumber(0)
    }

    const prevPage = pageNumber !== 0
    const nextPage = pageNumber + 1 !== pages

    return (
        <div
            {...({
                popover: 'auto',
            } as React.HTMLAttributes<HTMLDivElement>)}
            id="player-search"
            ref={ref}
            style={{
                height: '670px',
                maxHeight: '90%',
                maxWidth: '500px',
                width: '90%',
                background: 'whitesmoke',
            }}
        >
            <h1>Add A {typeText}</h1>
            {isFull && (
                <p style={{ color: '#c00', margin: '0 0 0.75rem' }}>
                    Maximum of 5 players reached. Remove a player to add
                    another.
                </p>
            )}
            <div>
                <input
                    className="form-control"
                    style={{ height: '32px', marginBottom: '15px' }}
                    type="text"
                    value={searchFilter}
                    onChange={(e) => handleFilter('search', e.target.value)}
                    disabled={type === 'league'}
                    placeholder={`Search by ${typeText} Name`}
                />
            </div>
            <div
                className="form-group"
                style={{ display: 'flex', gap: '0.5rem' }}
            >
                <div
                    className="savant-select"
                    data-title="Pitch Type"
                    style={{ flexBasis: '50%' }}
                >
                    <select
                        value={pitchTypeFilter}
                        disabled={!data[0].api_pitch_type_group03}
                        onChange={(e) =>
                            handleFilter('pitchType', e.target.value)
                        }
                    >
                        <option value="">Any</option>
                        {pitchTypes.map((pitch) => (
                            <option key={pitch} value={pitch}>
                                {getShortPitchName(pitch)}
                            </option>
                        ))}
                    </select>
                </div>
                <div
                    className="savant-select"
                    data-title="Pitch Hand"
                    style={{ flexBasis: '50%' }}
                >
                    <select
                        value={pitchHandFilter}
                        disabled={data.every((d) => d.pitch_hand_type === 'X')}
                        onChange={(e) =>
                            handleFilter('pitchHand', e.target.value)
                        }
                    >
                        <option value="">Any</option>
                        <option value="L">LHP</option>
                        <option value="R">RHP</option>
                    </select>
                </div>
            </div>
            <div>
                {sliced.map((d) => (
                    <PlayerCard
                        key={d.uniqueId}
                        row={d}
                        handleSelect={handleSelect}
                        disabled={isFull || selectedSet.has(d.uniqueId)}
                        groupByLogic={groupByLogic}
                        type={type}
                    />
                ))}
                {sliced.length === 0 && (
                    <div style={{ textAlign: 'center' }}>
                        <em>No Players Found</em>
                    </div>
                )}
            </div>
            <div style={{ textAlign: 'center', margin: '1rem' }}>
                <button
                    type="button"
                    onClick={() => setPageNumber(pageNumber - 1)}
                    disabled={!prevPage}
                    style={{
                        cursor: prevPage ? 'pointer' : 'not-allowed',
                    }}
                >
                    ←
                </button>
                <span style={{ display: 'inline-block', margin: '0 1rem' }}>
                    Page {pageNumber + 1}
                </span>
                <button
                    type="button"
                    onClick={() => setPageNumber(pageNumber + 1)}
                    disabled={!nextPage}
                    style={{
                        cursor: nextPage ? 'pointer' : 'not-allowed',
                    }}
                >
                    →
                </button>
            </div>
        </div>
    )
}
